import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { AplicationService } from '@services/aplication/aplication.service';
import { AuthService } from '@services/auth/auth.service';
import { MICROSERVICES } from '@shared/constants/microservices';
import { map, Observable } from 'rxjs';
import { ImpersonationSessionResponse } from '../domain/impersonation-session.model';
import { ImpersonationTargetsApiResponse, ImpersonationTargetsPaginatedResponse } from '../domain/impersonation-target.model';
import { Session, SessionImpersonationListResponse } from '../domain/session-impersonation-list.model';
import { CreateImpersonationSessionDto } from './dtos/create-session.dto';
import { SessionImpersonationListDto } from './dtos/session-impersonation-list.dto';
import { ImpersonationRepository } from './impersonation.repository';

@Injectable({
    providedIn: 'root',
})
export class ImpersonationApiService implements ImpersonationRepository {
    private readonly http = inject(HttpClient);
    private readonly authService = inject(AuthService);
    private readonly aplicationService = inject(AplicationService);
    private readonly baseUrl = `${environment.url}${MICROSERVICES.impersonation}`;

    private getHeaders(): HttpHeaders {
        const user = this.authService.user();
        const oid = user?.oid ?? '';
        const upn = user?.upn ?? '';
        const roles = this.aplicationService
            .aplications()
            .flatMap((aplication) => {
                const result: string[] = [];
                if (aplication.admin) {
                    result.push(`Road.Admin.${aplication.roadId}`);
                }
                if (aplication.impersonate.read) {
                    result.push(`Impersonate.Read.${aplication.roadId}`);
                }
                if (aplication.impersonate.write) {
                    result.push(`Impersonate.Write.${aplication.roadId}`);
                }
                return result;
            })
            .join(',');

        return new HttpHeaders({
            'x-app-user-id': oid,
            'x-app-user-email': upn,
            'x-user-roles': roles,
        });
    }

    getTargets(appId: string, page: number, limit: number, query?: string): Observable<ImpersonationTargetsPaginatedResponse> {
        let params = new HttpParams().set('appId', appId).set('page', page.toString()).set('limit', limit.toString());

        if (query) {
            params = params.set('query', query);
        }

        return this.http
            .get<ImpersonationTargetsApiResponse>(`${this.baseUrl}/impersonation-targets`, {
                params,
                headers: this.getHeaders(),
            })
            .pipe(
                map((raw) => {
                    const items = raw?.items ?? [];
                    const currentPage = raw?.page ?? 1;
                    const pageSize = raw?.limit ?? 50;
                    const totalElements = raw?.total ?? 0;
                    const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));

                    return {
                        data: items,
                        pagination: {
                            currentPage,
                            totalPages,
                            totalElements,
                            pageSize,
                        },
                    };
                })
            );
    }

    startSession(request: CreateImpersonationSessionDto): Observable<ImpersonationSessionResponse> {
        return this.http.post<ImpersonationSessionResponse>(`${this.baseUrl}/impersonation-sessions`, request, {
            headers: this.getHeaders(),
        });
    }

    sessionImpersionationList(request: SessionImpersonationListDto): Observable<SessionImpersonationListResponse> {
        let params = new HttpParams();

        if (request.actorId) {
            params = params.set('actorId', request.actorId);
        }

        if (request.targetUserId) {
            params = params.set('targetUserId', request.targetUserId);
        }

        if (request.appId) {
            params = params.set('appId', request.appId);
        }

        if (request.status) {
            params = params.set('status', request.status);
        }

        if (request.page !== undefined) {
            params = params.set('page', request.page.toString());
        }

        if (request.limit !== undefined) {
            params = params.set('limit', request.limit.toString());
        }

        return this.http.get<SessionImpersonationListResponse>(`${this.baseUrl}/impersonation-sessions`, {
            params,
            headers: this.getHeaders(),
        });
    }

    getSessionDetail(sessionId: string): Observable<Session> {
        return this.http.get<Session>(`${this.baseUrl}/impersonation-sessions/${sessionId}`, {
            headers: this.getHeaders(),
        });
    }
}
